import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { LayoutGrid, GanttChart, ClipboardCheck } from 'lucide-react';
import { fadeUp, Reveal } from './animations';

const text = {
  de: {
    label: 'Workflow',
    heading: 'Drei Ansichten. Dieselben Daten.',
    sub: 'Board bauen, Roadmap zeigen, Commitment absichern — ohne Werkzeugwechsel, ohne Doppelpflege.',
    steps: [
      {
        icon: LayoutGrid,
        num: '01',
        title: 'Board bauen',
        context: 'Discovery & Daily',
        body: 'Am Anfang jeder Projektphase und jeden Morgen im Daily: ein Board frei gebaut als Canvas. Flexible Lanes, Info-Boxen, Anwesenheit — die Struktur die das Team gerade braucht. Hier passiert die eigentliche Arbeit: Scope klären, Tasks strukturieren, Risiken aufdecken.',
      },
      {
        icon: GanttChart,
        num: '02',
        title: 'Roadmap zeigen',
        context: 'Team & Stakeholder',
        body: 'Dieselben Daten als Gantt-Timeline. Stakeholder sehen wann was fertig wird, wo Risiken liegen und wie sich Änderungen auswirken. Kein Extra-Tool, kein Nachpflegen — die Roadmap entsteht automatisch aus dem Board.',
      },
      {
        icon: ClipboardCheck,
        num: '03',
        title: 'Commitment absichern',
        context: 'Entscheidung',
        body: 'Die aggregierte Übersicht fasst alle Requirements, Deadlines und Konsequenzen zusammen. "Schneller" = höheres Risiko. "Weniger Leute" = längere Timeline. "Mehr Scope" = was wird verdrängt? Alles explizit für das finale Commitment.',
      },
    ],
  },
  en: {
    label: 'Workflow',
    heading: 'Three views. Same data.',
    sub: 'Build the board, show the roadmap, secure the commitment — no tool switching, no double maintenance.',
    steps: [
      {
        icon: LayoutGrid,
        num: '01',
        title: 'Build the board',
        context: 'Discovery & Daily',
        body: 'At the start of every project phase and every morning in the daily: a board built freely as a canvas. Flexible lanes, info boxes, attendance — whatever structure the team needs right now. This is where the real work happens: clarify scope, structure tasks, uncover risks.',
      },
      {
        icon: GanttChart,
        num: '02',
        title: 'Show the roadmap',
        context: 'Team & Stakeholders',
        body: 'Same data as a Gantt timeline. Stakeholders see when deliverables will be ready, where risks lie, and how changes affect the plan. No extra tool, no double maintenance — the roadmap is generated from the board automatically.',
      },
      {
        icon: ClipboardCheck,
        num: '03',
        title: 'Secure the commitment',
        context: 'Decision',
        body: 'The aggregated overview summarizes all requirements, deadlines, and consequences. "Faster" = higher risk. "Fewer people" = longer timeline. "More scope" = what gets displaced? Everything explicit for the final commitment.',
      },
    ],
  },
} as const;

export function LandingWorkflowSection() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
  const t = text[lang];

  return (
    <section className="landing-workflow-section">
      <Reveal className="landing-workflow-inner">
        <motion.p className="landing-section-label" variants={fadeUp} custom={0}>
          {t.label}
        </motion.p>
        <motion.h2 className="landing-section-heading" variants={fadeUp} custom={1}>
          {t.heading}
        </motion.h2>
        <motion.p className="landing-section-sub" variants={fadeUp} custom={2}>
          {t.sub}
        </motion.p>
        <motion.div className="landing-workflow-grid" variants={fadeUp} custom={3}>
          {t.steps.map((step) => (
            <div key={step.num} className="landing-workflow-card">
              <div className="landing-workflow-card-head">
                <span className="landing-workflow-num">{step.num}</span>
                <step.icon size={18} strokeWidth={1.5} className="landing-workflow-icon" />
              </div>
              <h3 className="landing-workflow-card-title">{step.title}</h3>
              <span className="landing-workflow-card-context">{step.context}</span>
              <p className="landing-workflow-card-body">{step.body}</p>
            </div>
          ))}
        </motion.div>
      </Reveal>
    </section>
  );
}
